import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Rocket, Lock, Eye, EyeOff, Building2, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import axios from 'axios';
import { formatErrorDetail } from '../utils/formatError';

const AVATARS = [
  { id: 'a1', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix&backgroundColor=f59e0b' },
  { id: 'a2', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka&backgroundColor=f59e0b' },
  { id: 'a3', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Riley&backgroundColor=f59e0b' },
  { id: 'a4', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jordan&backgroundColor=f59e0b' },
  { id: 'a5', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Alex&backgroundColor=f59e0b' },
  { id: 'a6', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Taylor&backgroundColor=f59e0b' },
];

const SpringboardAccess = () => {
  const navigate = useNavigate();
  const { user, token, login, register } = useAuth();
  const [mode, setMode] = useState('login'); // login | register
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome to your Springboard');
      navigate('/portal/tasks');
    } catch (error) {
      toast.error(formatErrorDetail(error.response?.data?.detail) || 'Invalid credentials');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (!businessName.trim()) { toast.error('Please enter your business name'); return; }
    setLoading(true);
    try {
      // Register as hub_only user
      const res = await axios.post(`${API}/auth/register`, {
        name, email, password, avatar,
        user_type: 'hub_only',
        business_name: businessName,
      });
      // Log them in
      localStorage.setItem('token', res.data.token);
      // Demo bypass for hub subscription
      await axios.post(`${API}/payments/demo-bypass`, { package_id: 'springboard_hub' }, {
        headers: { Authorization: `Bearer ${res.data.token}` },
      });
      toast.success('Hub account created');
      window.location.href = '/portal/tasks';
    } catch (error) {
      toast.error(formatErrorDetail(error.response?.data?.detail) || 'Registration failed');
    }
    setLoading(false);
  };

  const goToSpringboard = () => navigate('/portal/tasks');

  return (
    <div style={{ minHeight: '100vh', background: '#fffbeb', fontFamily: 'Arial, sans-serif' }}>
      {/* Nav */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        background: 'rgba(255, 251, 235, 0.95)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '12px 24px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            onClick={() => navigate('/')}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#92400e' }}
          >
            <ArrowLeft style={{ width: 16, height: 16 }} />
            Back to SPRING
          </button>
          <span style={{ fontSize: '20px', fontWeight: 800, color: '#92400e', letterSpacing: '-0.5px' }}>
            Springboard
          </span>
          <div style={{ width: 100 }} />
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: '480px', margin: '0 auto', padding: '120px 24px 60px', textAlign: 'center' }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: 0,
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px', boxShadow: '0 4px 20px rgba(245, 158, 11, 0.3)'
        }}>
          <Rocket style={{ width: 28, height: 28, color: '#fff' }} />
        </div>

        <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#1E491A', marginBottom: '12px', letterSpacing: '-0.5px' }} data-testid="springboard-access-heading">
          The Springboard
        </h1>
        <p style={{ fontSize: '15px', color: '#666', lineHeight: 1.6, marginBottom: '36px' }}>
          Your private workspace. Tasks, documents, calendar and everything your business needs, in one place.
        </p>

        {user ? (
          <div>
            <p style={{ fontSize: '14px', color: '#92400e', marginBottom: '16px' }}>
              Welcome back, <strong>{user.name}</strong>.
            </p>
            <Button
              onClick={goToSpringboard}
              className="w-full rounded-none"
              style={{ background: '#d97706', color: '#fff', padding: '14px 28px', fontSize: '15px', fontWeight: 600 }}
              data-testid="springboard-enter-btn"
            >
              <Rocket className="h-4 w-4 mr-2" />
              Enter Your Springboard
            </Button>
          </div>
        ) : (
          <>
            {/* Login / Register toggle */}
            <div style={{ display: 'flex', gap: '4px', background: '#f5f0e1', borderRadius: 0, padding: '4px', marginBottom: '24px' }}>
              <button
                onClick={() => setMode('login')}
                data-testid="springboard-tab-login"
                style={{
                  flex: 1, padding: '10px', borderRadius: 0, border: 'none',
                  background: mode === 'login' ? '#fff' : 'transparent',
                  color: mode === 'login' ? '#1a1a1a' : '#92400e',
                  fontWeight: 600, fontSize: '14px', cursor: 'pointer',
                  boxShadow: mode === 'login' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                Sign In
              </button>
              <button
                onClick={() => setMode('register')}
                data-testid="springboard-tab-register"
                style={{
                  flex: 1, padding: '10px', borderRadius: 0, border: 'none',
                  background: mode === 'register' ? '#fff' : 'transparent',
                  color: mode === 'register' ? '#1a1a1a' : '#92400e',
                  fontWeight: 600, fontSize: '14px', cursor: 'pointer',
                  boxShadow: mode === 'register' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                <Building2 style={{ width: 14, height: 14, display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                Hub Sign Up
              </button>
            </div>

            {mode === 'login' ? (
              <form onSubmit={handleLogin}>
                <div style={{
                  background: '#fff', borderRadius: 0, padding: '28px',
                  border: '1px solid rgba(0,0,0,0.08)', textAlign: 'left',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                    <Lock style={{ width: 14, height: 14, color: '#d97706' }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>Sign in to access your Springboard</span>
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Email</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      data-testid="springboard-email"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e5e5e5', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                      placeholder="you@example.com" />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Password</label>
                    <div style={{ position: 'relative' }}>
                      <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required
                        data-testid="springboard-password"
                        style={{ width: '100%', padding: '10px 40px 10px 14px', borderRadius: '10px', border: '1px solid #e5e5e5', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                        placeholder="Your password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#999' }}>
                        {showPassword ? <EyeOff style={{ width: 16, height: 16 }} /> : <Eye style={{ width: 16, height: 16 }} />}
                      </button>
                    </div>
                  </div>

                  <button type="submit" disabled={loading} data-testid="springboard-login-btn"
                    style={{ width: '100%', padding: '12px', borderRadius: 0, background: '#d97706', color: '#fff', border: 'none', fontSize: '15px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleRegister}>
                <div style={{
                  background: '#fff', borderRadius: 0, padding: '28px',
                  border: '1px solid rgba(0,0,0,0.08)', textAlign: 'left',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                    <Building2 style={{ width: 14, height: 14, color: '#d97706' }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>Register for Hub access — $60/mo</span>
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Your Name</label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} required
                      data-testid="springboard-reg-name"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e5e5e5', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                      placeholder="Your name" />
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Business Name</label>
                    <input type="text" value={businessName} onChange={(e) => setBusinessName(e.target.value)} required
                      data-testid="springboard-reg-business"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e5e5e5', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                      placeholder="Your business name" />
                  </div>

                  {/* Avatar */}
                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '8px' }}>Avatar</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {AVATARS.map((av) => (
                        <button key={av.id} type="button" onClick={() => setAvatar(av.url)}
                          data-testid={`springboard-avatar-${av.id}`}
                          style={{
                            width: '44px', height: '44px', borderRadius: '50%',
                            border: avatar === av.url ? '3px solid #d97706' : '2px solid #e5e5e5',
                            padding: '2px', cursor: 'pointer', overflow: 'hidden', background: '#f8f8f8',
                            transition: 'border-color 0.15s',
                          }}>
                          <img src={av.url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Email</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      data-testid="springboard-reg-email"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e5e5e5', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                      placeholder="you@business.com" />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Password</label>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                      data-testid="springboard-reg-password"
                      style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e5e5e5', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                      placeholder="At least 6 characters" />
                  </div>

                  <button type="submit" disabled={loading} data-testid="springboard-register-btn"
                    style={{ width: '100%', padding: '12px', borderRadius: 0, background: '#d97706', color: '#fff', border: 'none', fontSize: '15px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" style={{ margin: '0 auto' }} /> : 'Create Hub Account'}
                  </button>
                </div>

                <p style={{ fontSize: '12px', color: '#92400e', marginTop: '12px', opacity: 0.7 }}>
                  +$25/mo per additional team seat
                </p>
              </form>
            )}

            <p style={{ fontSize: '13px', color: '#999', marginTop: '20px' }}>
              {mode === 'login' ? (
                <>
                  Need to start from the beginning?{' '}
                  <button type="button" onClick={() => navigate('/auth')}
                    style={{ background: 'none', border: 'none', color: '#d97706', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
                    Take the free assessment
                  </button>
                </>
              ) : (
                <>
                  Already started the SPRING journey?{' '}
                  <button type="button" onClick={() => navigate('/auth')}
                    style={{ background: 'none', border: 'none', color: '#d97706', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
                    Sign in here
                  </button>
                </>
              )}
            </p>
          </>
        )}

        {/* Features preview */}
        <div style={{ marginTop: '48px', textAlign: 'left' }}>
          <p style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#92400e', marginBottom: '16px' }}>
            What's inside
          </p>
          {[
            { label: 'Dashboard', desc: 'See where you are and what is next' },
            { label: 'Tasks', desc: 'Every action tracked and ticked off' },
            { label: 'Documents', desc: 'Plans, templates and files, safe and organised' },
            { label: 'Business Apps', desc: 'Tools and partners your business needs' },
            { label: 'Business Dashboard', desc: 'Sales, leads and key metrics at a glance' },
            { label: 'Team Seats', desc: 'Add team members at $25/seat/mo' },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex', gap: '12px', alignItems: 'flex-start',
              padding: '10px 0', borderBottom: i < 5 ? '1px solid rgba(0,0,0,0.05)' : 'none'
            }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#d97706', marginTop: '7px', flexShrink: 0 }} />
              <div>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#1E491A', margin: 0 }}>{item.label}</p>
                <p style={{ fontSize: '13px', color: '#888', margin: '2px 0 0' }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SpringboardAccess;
